import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Ctx = createContext(null);

export function CrawlerProvider({ children }) {
  const [crawlers, setCrawlers] = useState([]);
  const [queue, setQueue] = useState({ running: null, queued: [], pending: 0 });
  const [runAllTotal, setRunAllTotal] = useState(0);
  const [optimistic, setOptimistic] = useState(() => new Set());

  const busy = !!queue.running || queue.queued.length > 0 || queue.pending > 0;
  const remaining = (queue.running ? 1 : 0) + queue.queued.length;
  const doneCount = runAllTotal > 0 ? Math.max(0, runAllTotal - remaining) : 0;
  const progressPct = runAllTotal > 0 ? Math.round((doneCount / runAllTotal) * 100) : 0;

  const reload = useCallback(async () => {
    try {
      const [c, qs] = await Promise.all([api.get("/crawlers"), api.get("/queue/status")]);
      setCrawlers(c.data);
      setQueue(qs.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, busy ? 2000 : 5000);
    return () => clearInterval(t);
  }, [reload, busy]);

  useEffect(() => {
    if (!busy) { setRunAllTotal(0); return; }
    setRunAllTotal((prev) => (remaining > prev ? remaining : prev));
  }, [busy, remaining]);

  useEffect(() => {
    setOptimistic((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      prev.forEach((code) => {
        if (queue.running === code || queue.queued.includes(code)) { next.delete(code); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [queue]);

  const markOptimistic = useCallback((codes) => {
    setOptimistic((prev) => { const n = new Set(prev); codes.forEach((c) => n.add(c)); return n; });
    window.setTimeout(() => setOptimistic((prev) => { const n = new Set(prev); codes.forEach((c) => n.delete(c)); return n; }), 6000);
  }, []);

  const statusOf = useCallback((c) => {
    if (queue.running === c.code) return "running";
    if (queue.queued.includes(c.code)) return "queued";
    if (optimistic.has(c.code)) return "queued";
    return c.last_status || "idle";
  }, [queue, optimistic]);

  const runOne = useCallback(async (code) => {
    if (queue.running === code || queue.queued.includes(code) || optimistic.has(code)) return;
    markOptimistic([code]);
    try { await api.post(`/crawlers/${code}/run`); toast.success(`Crawler ${code} eingereiht`); reload(); }
    catch { toast.error("Start fehlgeschlagen"); setOptimistic((p) => { const n = new Set(p); n.delete(code); return n; }); }
  }, [queue, optimistic, markOptimistic, reload]);

  const runAll = useCallback(async () => {
    markOptimistic(crawlers.map((c) => c.code));
    try {
      const r = await api.post("/crawlers/run-all");
      setRunAllTotal(r.data.count || 0);
      toast.success(`${r.data.count} Crawler eingereiht`, { description: "Läuft sequentiell. Bereits laufende werden übersprungen." });
      reload();
    } catch { toast.error("Konnte nicht starten"); setOptimistic(new Set()); }
  }, [crawlers, markOptimistic, reload]);

  const value = useMemo(() => ({
    crawlers, queue, busy, runAllTotal, doneCount, progressPct, remaining,
    statusOf, runOne, runAll, reload,
  }), [crawlers, queue, busy, runAllTotal, doneCount, progressPct, remaining, statusOf, runOne, runAll, reload]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCrawlers = () => useContext(Ctx);
